import {Component, Input, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {EventService} from "../../services/event.service";
import {Scoreboard, SpotfightEvent, SpotfightQuestion, UserScore, Vote, Votes} from "../../core/models/models";

import {SpotfightDateFormatPipe} from "../../core/pipes/date-format/spotfight-date-format.pipe";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {VoteService} from "../../services/vote.service";
import {ToastService} from "../../core/notifications/toast-service";
import {ImageService} from "../../services/image.service";
import {AuthenticationService} from "../../services/authentication.service";
import {ScoreTableComponent} from "../score-table/score-table.component";
import {ScoreboardService} from "../../services/scoreboard.service";
import {UserRankComponent} from "../user-rank/user-rank.component";
import {CountdownComponent} from "../countdown/countdown.component";
import {LinkAccountHintComponent} from "../link-account-hint/link-account-hint.component";
import {hasLinkedAccount} from "../../core/utils";
import {Router} from "@angular/router";
import {LinkAccountComponent} from "../link-account/link-account.component";

@Component({
  selector: 'app-single-event',
  standalone: true,
  imports: [
    SpotfightDateFormatPipe,
    ReactiveFormsModule,
    FormsModule,
    ScoreTableComponent,
    UserRankComponent,
    CountdownComponent,
    LinkAccountHintComponent,
    LinkAccountComponent
],
  templateUrl: './single-event.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './single-event.component.scss'
})
export class SingleEventComponent implements OnInit {
  @Input()
  league = "";

  @Input()
  uuid = "";

  event!: SpotfightEvent;
  userScore: UserScore | undefined;
  scoreboard: Scoreboard | undefined;
  showHintForVotes = false;
  votes: Votes | undefined;

  @ViewChild(LinkAccountComponent) linkAccountComponent!: LinkAccountComponent;

  constructor(private eventService: EventService,
              private voteService: VoteService,
              private toastService: ToastService,
              private authenticationService: AuthenticationService,
              private imageService: ImageService,
              private scoreboardService: ScoreboardService,
              private router: Router) {
  }


  ngOnInit(): void {
    let fetchEventPromise = this.eventService.getEvent(this.league, this.uuid)
      .then(event => {
          this.event = event;
          if (this.event.image_uri !== undefined && event.image_uri !== "") {
            this.loadImage(event);
          }
          this.loadVotes();
        }
      )
      .catch(() => {
        this.toastService.showDanger("Event konnte nicht geladen werden!");
      });

    this.scoreboardService.getScoreboardForEvent(this.uuid)
      .then((scoreboardOverall) => this.scoreboard = scoreboardOverall)
      .catch(() => this.toastService.showDanger("Scoreboard konnte nicht geladen werden!"));

    this.scoreboardService.getUserScoreEvent(this.uuid)
      .then((userScore) => {
        this.userScore = userScore;
      })
      .catch(() => this.toastService.showDanger("User Score konnte nicht geladen werden!"));
  }

  private loadVotes() {
    this.voteService.getOwnVotesForEvent(this.uuid).then((votes) => {
      this.votes = votes;
      this.event.questions.forEach(question => {
        if (votes.votes.hasOwnProperty(question.uuid)) {
          // If a match is found, store the vote count in `question.selected`
          question.selected = votes.votes[question.uuid];
        }
      });
    }).catch((error) => {
      if (error && error.status === 404 && !this.event.closed) {
        if (!this.isDeadlineOver(this.event.deadline)) {
          this.showHintForVotes = true;
        }
      }
    })
  }

  private loadImage(event: SpotfightEvent) {
    this.imageService.getImage(event.image_uri)
      .then((value) => {
        event.image_content = value;
      })
      .catch(() => console.log("Error loading image for event"));
  }

  async onSubmitVotes() {
    if (!hasLinkedAccount(this.authenticationService)) {
      this.linkAccountComponent.show();
    } else {
      const user = this.authenticationService.getUser()!;
      const userId = user.user_id;
      const nickName = user.full_name;
      const newVote: Votes = {
        event_uuid: this.event.uuid,
        user_id: userId,
        display_name: nickName,
        votes: this.event.questions.reduce((acc, value) => {
          acc[value.uuid] = value.selected!;
          return acc;
        }, {} as Vote)
      };

      this.voteService.putVote(newVote, this.event.league)
        .then((value) => {
          this.showHintForVotes = false;
          this.toastService.showSuccess("Tipp gespeichert!");
        })
        .catch(() => this.toastService.showDanger("Tipp konnte nicht gespeichert werden!"));
    }
  }


  isDeadlineOver(deadline: Date): boolean {
    const now = new Date();
    return new Date(deadline) < now;
  }

  isEditable() {
    if (this.event.closed) {
      return false;
    }

    return !this.isDeadlineOver(new Date(this.event.deadline))
  }

  isClosed() {
    return this.event.closed;
  }

  isQuestionAnsweredRight(question: SpotfightQuestion) {
    if (this.votes !== undefined) {
      return question.correct == this.votes.votes[question.uuid];
    }

    return false;
  }

  loadMore() {
    if (this.scoreboard?.last_key == undefined) {
      this.toastService.showDanger("Keine weiteren Einträge vorhanden!")
      return;
    }

    const lastKey = this.scoreboard!.last_key!;

    this.scoreboardService.getScoreboardForEventWithLastKey(this.uuid, lastKey).then(
      (scoreboard) => this.updateScoreboard(scoreboard))
      .catch(() => this.toastService.showDanger("No scoreboards found"));

  }

  private updateScoreboard(scoreboard: Scoreboard) {
    this.scoreboard!.scores = this.scoreboard!.scores.concat(scoreboard.scores);
    this.scoreboard!.last_key = scoreboard.last_key;
  }
}
